import { useT } from '../../i18n/index.jsx';
import Icon from './Icon';
import { S } from '../../config/theme.js';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

interface TourStep {
  title: string;
  text: string;
  icon: string;
  target: string;
  shortcut?: string;
}

interface OnboardingTourProps {
  step: number;
  setStep: (step: number) => void;
  onFinish: () => void;
  rolle: string;
}

// Onboarding-Tour: 3-4 Tooltips beim ersten Login
/**
 * OnboardingTour — Schritt-für-Schritt Onboarding-Tour für neue Benutzer.
 */
export function OnboardingTour({ step, setStep, onFinish, rolle }: OnboardingTourProps): React.JSX.Element | null {
  const t = useT();
  const STEPS_FIRMA: TourStep[] = [
    { title: t('obFirmaWelcomeTitle'), text: t('obFirmaWelcomeText'), icon: 'package', target: 'auftraege' },
    {
      title: t('obFirmaCreateTitle'),
      text: t('obFirmaCreateText'),
      icon: 'document-edit',
      target: 'neuerAuftrag',
      shortcut: 'N',
    },
    { title: t('obFirmaStatsTitle'), text: t('obFirmaStatsText'), icon: 'chart', target: 'abrechnung', shortcut: 'S' },
    { title: t('obFirmaFavTitle'), text: t('obFirmaFavText'), icon: 'star', target: 'profil', shortcut: 'P' },
  ];
  const STEPS_FAHRER: TourStep[] = [
    { title: t('obFahrerWelcomeTitle'), text: t('obFahrerWelcomeText'), icon: 'map', target: 'marktplatz' },
    { title: t('obFahrerOfferTitle'), text: t('obFahrerOfferText'), icon: 'money-bag', target: 'angebot' },
    {
      title: t('obFahrerOrdersTitle'),
      text: t('obFahrerOrdersText'),
      icon: 'package',
      target: 'meineauftraege',
      shortcut: 'M',
    },
    { title: t('obFahrerCertTitle'), text: t('obFahrerCertText'), icon: 'id-card', target: 'profil', shortcut: 'P' },
  ];
  const steps = rolle === 'firma' ? STEPS_FIRMA : STEPS_FAHRER;
  if (step < 0 || step >= steps.length) return null;
  const s = steps[step];
  const isLast = step === steps.length - 1;
  return (
    <div>
      {// Backdrop
      <div
        onClick={onFinish}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,.5)',
          backdropFilter: 'blur(3px)',
          zIndex: 250,
          animation: 'fadeIn .3s ease',
        }} />}
      {// Tooltip
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: 251,
          width: 400,
          maxWidth: '92vw',
          animation: 'fadeInScale .35s cubic-bezier(.2,0,.1,1)',
        }}>
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 20,
            padding: '28px 28px 22px',
            boxShadow: '0 24px 60px rgba(0,0,0,.4), 0 0 0 1px rgba(74,222,128,.08)',
            position: 'relative',
            overflow: 'hidden',
          }}>
          {// Accent top bar
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: 'linear-gradient(90deg, var(--accent), var(--amber, #f59e0b))',
            }} />}
          {// Step counter
          <div style={Object.assign({}, S.rowBetween, { marginBottom: 18 })}>
            <div style={Object.assign({}, S.row, { gap: 6 })}>
              {steps.map(function (_, i) {
                return (
                  <div
                    key={i}
                    style={{
                      width: i === step ? 24 : 8,
                      height: 8,
                      borderRadius: 4,
                      background: i <= step ? 'var(--accent)' : 'var(--border)',
                      transition: 'all .3s',
                    }} />
                );
              })}
            </div>
            <button
              onClick={onFinish}
              style={Object.assign({}, S.caption, {
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
              })}>
              {t('obSkip')}
            </button>
          </div>}
          {// Icon
          <div
            style={{ fontSize: 40, marginBottom: 14, animation: 'float 3s ease-in-out infinite' }}>
            <Icon name={s.icon} size={40} />
          </div>}
          {// Title
          <div
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 18,
              fontWeight: 800,
              marginBottom: 8,
              letterSpacing: '-.3px',
            }}>
            {s.title}
          </div>}
          {// Text
          <div
            style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 6 }}>
            {s.text}
          </div>}
          {// Shortcut hint
          s.shortcut
            ? <div
            style={Object.assign({}, S.row, { fontSize: 12, color: 'var(--muted2)', marginBottom: 16, gap: 6 })}>
            {t('obShortcut')}
            <kbd
              style={Object.assign({}, S.label, {
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '2px 8px',
                fontFamily: "'JetBrains Mono',monospace",
              })}>
              {s.shortcut}
            </kbd>
          </div>
            : null}
          {// Actions
          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}>
            {step > 0
              ? <button
              onClick={function () {
                setStep(step - 1);
              }}
              style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                borderRadius: 10,
                padding: '10px 20px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'DM Sans',sans-serif",
              }}>
              {t('zurueck')}
            </button>
              : null}
            <button
              onClick={function () {
                if (isLast) {
                  onFinish();
                } else {
                  setStep(step + 1);
                }
              }}
              style={{
                background: isLast
                  ? 'linear-gradient(135deg,#d97706,#f59e0b)'
                  : 'linear-gradient(135deg,#15803d,#22c55e)',
                border: 'none',
                color: '#fff',
                borderRadius: 10,
                padding: '10px 24px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'DM Sans',sans-serif",
                boxShadow: '0 4px 16px ' + (isLast ? 'rgba(245,158,11,.3)' : 'rgba(22,163,74,.25)'),
              }}>
              {isLast ? t('obGo') : t('weiter')}
            </button>
          </div>}
        </div>
      </div>}
    </div>
  );
}
